import { MARKETABLE_CONTACTS_WRITE } from 'pricing-pages-lib/constants/Scopes';
import { getQueryParams, stringifyQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { getPricingPageProductRoute } from 'pricing-pages-lib/utils/urls';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { REQUEST_MC_WRITE } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import PricingPageInterstitialBuyContactsButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuyContactsButton';
import PricingPageInterstitialBuyNowButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuyNowButton';
import PricingPageInterstitialBuySeatsButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuySeatsButton';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, CALCULATE_CONTACTS, CALCULATE_PRICE, CALCULATE_SEATS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIAdminOnlyWrapper from 'ui-addon-upgrades/decorators/UIAdminOnlyWrapper';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { shouldShowEligibilityFlowSelector } from '../../../selectors/MarketingContactsSelectors';
import { configsWithSuiteIfPossibleSelector, productsAndPacksForQueryParamsSelector, sortedProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { ownsCrmSuiteProductLevel, productsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { bundledProductLinesForSkuIdSelector, isCoreSeatSkuSelector, isQuantifiableHubSelector, productForSkuIdSelector, skuIdIsQuantityPackSelector } from '../../../selectors/ProductsSelectors';
import { trackingProductNameSelector } from '../../../selectors/TrackingSelectors';
import { getBuyNowButtonClickEvent } from '../../../utils/event-tracking/interactions';
import { trackEvent } from '../../../utils/event-tracking/track-event';
import { jsx as _jsx } from "react/jsx-runtime";
const InterstitialPurchaseMotionButtonWrapper = props => {
  const {
    purchaseMotions,
    level,
    nonMerchandiseIdIdentifier,
    productConfigurations = []
  } = props;
  const sortedProductConfigurations = useAppSelector(state => sortedProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const productConfiguration = sortedProductConfigurations[0];
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productConfiguration.skuId
  }));
  const {
    apiName
  } = product;
  const history = useHistory();
  const purchaseMotion = purchaseMotions[level - 1];
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const hasMarketingProduct = useAppSelector(state => {
    const marketingConfig = productConfigurations && productConfigurations.find(({
      skuId
    }) => {
      const bundledProductLines = bundledProductLinesForSkuIdSelector(state, {
        skuId
      });
      return [product.productLine, ...bundledProductLines].includes(MARKETING);
    });
    return marketingConfig && productForSkuIdSelector(state, {
      skuId: marketingConfig.skuId
    });
  });
  const trackingProductNames = useSelector(state => productConfigurations.map(({
    skuId
  }) => trackingProductNameSelector(state, {
    skuId
  })));
  const ownedProducts = useSelector(state => {
    return productsFromProductInventorySelector(state);
  }).map(p => p.apiName);
  const productConfigurationsWithSuiteIfNecessary = useAppSelector(state => {
    // keep only seat configs for buying additional seats
    if (purchaseMotion === BUY_ADDITIONAL_SEATS) {
      return productConfigurations.filter(config => isCoreSeatSkuSelector(state, {
        skuId: config.skuId
      }) || isQuantifiableHubSelector(state, {
        skuId: config.skuId
      }) || skuIdIsQuantityPackSelector(state, {
        skuId: config.skuId
      }) && config.quantity > 0);
    }

    // Temporary workaround - you cannot buy a net new child item without including
    // the parent item. In the case of marketing contacts, if you own suite,
    // productConfigurations needs to contain the rest of the suite products
    // to take to checkout. This can be removed when upgrade-catalog can
    // handle quantity increases and net new upgrades together.
    if (purchaseMotion === BUY_ADDITIONAL_CONTACTS || hasMarketingProduct) {
      const {
        productLevel
      } = hasMarketingProduct;
      const ownedSuiteProductLevel = ownsCrmSuiteProductLevel(state, {
        productLevel: productLevel
      });
      if (ownedSuiteProductLevel) {
        return configsWithSuiteIfPossibleSelector(state, {
          productConfigurations
        });
      }
    }
    return productConfigurations;
  });
  const productConfigurationsAsQueryParams = useAppSelector(state => productsAndPacksForQueryParamsSelector(state, {
    productConfigurations: productConfigurationsWithSuiteIfNecessary
  }));
  const shouldShowEligibilityFlow = useAppSelector(shouldShowEligibilityFlowSelector);
  const seleniumId = nonMerchandiseIdIdentifier || apiName;
  const propsToPass = Object.assign({}, props, {
    seleniumId
  });
  const onClick = e => {
    if (props.onClick) {
      props.onClick(e);
    }
    const queryParams = getQueryParams();
    const pricingPageRoute = getPricingPageProductRoute(isAppAuth);
    const stringifiedParams = stringifyQueryParams(Object.assign({}, queryParams, productConfigurationsAsQueryParams, {
      pricingPageProductRoute: pricingPageRoute || '',
      purchaseMotion
    }));
    trackEvent(getBuyNowButtonClickEvent({
      productNames: trackingProductNames,
      ownedProducts,
      location: props.location,
      screen: props.screen
    }));
    history.push(`/interstitial?${stringifiedParams}`);
  };
  if (purchaseMotion === BUY_TOUCHLESS || purchaseMotion === CALCULATE_PRICE) {
    if (hasMarketingProduct && shouldShowEligibilityFlow) {
      return /*#__PURE__*/_jsx(UIAdminOnlyWrapper, {
        adminType: MARKETABLE_CONTACTS_WRITE,
        modalType: REQUEST_MC_WRITE,
        upgradeData: props.upgradeData,
        children: /*#__PURE__*/_jsx(PricingPageInterstitialBuyNowButton, Object.assign({}, propsToPass, {
          onClick: onClick
        }))
      });
    }
    return /*#__PURE__*/_jsx(PricingPageInterstitialBuyNowButton, Object.assign({}, propsToPass, {
      onClick: onClick
    }));
  }
  if (purchaseMotion === BUY_ADDITIONAL_CONTACTS || purchaseMotion === CALCULATE_CONTACTS) {
    if (shouldShowEligibilityFlow) {
      return /*#__PURE__*/_jsx(UIAdminOnlyWrapper, {
        adminType: MARKETABLE_CONTACTS_WRITE,
        modalType: REQUEST_MC_WRITE,
        upgradeData: props.upgradeData,
        children: /*#__PURE__*/_jsx(PricingPageInterstitialBuyContactsButton, Object.assign({}, propsToPass, {
          onClick: onClick
        }))
      });
    }
    return /*#__PURE__*/_jsx(PricingPageInterstitialBuyContactsButton, Object.assign({}, propsToPass, {
      onClick: onClick
    }));
  }
  if (purchaseMotion === BUY_ADDITIONAL_SEATS || purchaseMotion === CALCULATE_SEATS) {
    return /*#__PURE__*/_jsx(PricingPageInterstitialBuySeatsButton, Object.assign({}, propsToPass, {
      onClick: onClick
    }));
  }
  return null;
};
export default InterstitialPurchaseMotionButtonWrapper;